import React, { useEffect, useState, useRef } from 'react'
import { currency } from 'lib/utils/string'
import { useSnackbar } from 'notistack'
import { useForm, FormProvider } from 'react-hook-form'
import { validationSchema } from '../utils'
import { yupResolver } from '@hookform/resolvers'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Button from '@material-ui/core/Button'
import { useAuth } from 'lib/auth'
import { useStyles } from './styles'
import { gtmEvent } from 'lib/gtm'
import BackButton from 'components/back-button'
import themes, { extendTheme } from 'themes'
import { ThemeProvider } from '@material-ui/core'
import InitialsAvatar from 'components/avatars/initials-avatar'
import PropTypes from 'prop-types'
import ProgressIndicator from 'components/progress-indicators/progress-indicator'
import StepDescriptionOfferings from '../components/step-description-offerings'
import CoachLessonHeader from '../components/CoachLessonHeader'
import { ContainerRoot, WaiverContainer } from '../components/styles'
import { COACHING_CENTER_SLUGS } from 'utils/constants'
import { CoachingCenterWaiver } from 'components/waivers'
import MarketingOptIn from 'components/marketing-opt-in/marketing-opt-in'
import StripePaymentElementsForm from './stripe-payment-elements-form'
import { usePrepareLessonForStudent } from './hooks'
import Error from 'components/error'
import { useParams } from 'react-router-dom'
import { amplitudeEvent } from 'lib/amplitude'
import RouterLinkButton from 'components/router-link-button'

const LessonReviewLayout = ({
  lessonType,
  locationId,
  startDateTime,
  student,
  previousUrl,
  isComplete,
  setIsComplete,
  progressValue,
  stepText,
  eventObject = {},
}) => {
  const { priceInCents, coach, facilities, acceptsOnlinePayments } = lessonType
  const facility = facilities.find(({ id }) => id === locationId)

  const studentName = `${student.firstName} ${student.lastName}`
  const priceInDollars = priceInCents ? priceInCents / 100 : null

  const { user } = useAuth()
  const { idOrSlug } = useParams()
  const coachBooksOnBehalfOfStudent =
    user?.externalId === lessonType?.coach.externalId
  const shouldShowWaiver =
    COACHING_CENTER_SLUGS.includes(lessonType?.academy?.slug) &&
    !coachBooksOnBehalfOfStudent
  const [showWaiver, setShowWaiver] = useState(false)
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [showWaiver])

  const canSkipPayment = user && user?.id === lessonType?.coach?.id
  const [paymentRequired, setPaymentRequired] = useState(
    !canSkipPayment && acceptsOnlinePayments,
  )

  const bookNewSessionUrl = `/coach/${idOrSlug}/schedule?lessonTypeId=${lessonType?.id}&locationId=${locationId}`

  useEffect(() => {
    amplitudeEvent('view-checkout-screen', {
      payment_required: paymentRequired,
      purchase_type: 'private_booking',
      payment_intent_enabled: true,
    })
  }, [paymentRequired])

  const userIsNotOptedIn = !user?.marketingOptInAt
  const [marketingOptInChecked, setMarketingOptInChecked] = useState(false)

  const { enqueueSnackbar } = useSnackbar()
  const stripePaymentFormRef = useRef(null)

  // Delay usePrepareLessonForStudent until "Complete Purchase" is clicked
  const shouldDelayClientSecretFetch = !paymentRequired
  const [shouldFetchClientSecret, setShouldFetchClientSecret] = useState(
    !shouldDelayClientSecretFetch,
  )
  const [pendingButtonAction, setPendingButtonAction] = useState(null)

  const {
    clientSecret,
    setClientSecret,
    loading: clientSecretLoading,
    error: clientSecretError,
    success: prepareLessonSuccess,
    message: prepareLessonMessage,
  } = usePrepareLessonForStudent({
    lessonTypeId: lessonType.id,
    locationId: facility?.id,
    startDateTime,
    studentId: student.id,
    skipPayment: !paymentRequired,
    enabled: shouldFetchClientSecret,
    shouldExcludeSkipPayment:
      !priceInCents || priceInCents === 0 || !acceptsOnlinePayments,
  })

  // When skipPayment is toggled, manage fetching state
  useEffect(() => {
    if (paymentRequired && !shouldFetchClientSecret) {
      setShouldFetchClientSecret(true)
    } else if (!paymentRequired && shouldFetchClientSecret) {
      setShouldFetchClientSecret(false)
      setClientSecret(null)
    }
  }, [paymentRequired, shouldFetchClientSecret, setClientSecret])

  // Proceed with pending action once client secret is available
  useEffect(() => {
    // For skipPayment case, we need loading to complete and check success status
    // For payment required case, we need clientSecret to exist
    const isSkipPaymentCase = shouldDelayClientSecretFetch && !paymentRequired

    // Only proceed if loading is complete and we have a definitive result
    const loadingComplete = !clientSecretLoading
    const hasResult = isSkipPaymentCase
      ? prepareLessonSuccess !== null // For skip payment, we need success status (true or false)
      : !!clientSecret // For payment required, we need clientSecret

    const canProceed = pendingButtonAction && loadingComplete && hasResult

    if (canProceed) {
      const proceedWithAction = async () => {
        // When skipPayment is true and we delayed the fetch, purchase is already handled in prepareLessonForStudentHook
        // Set isComplete to show SummaryLayout instead of redirecting
        if (shouldDelayClientSecretFetch && !paymentRequired) {
          if (prepareLessonSuccess === true) {
            enqueueSnackbar('Your session has been booked.', {
              variant: 'success',
            })
            gtmEvent({
              ...eventObject,
              event: 'complete-private-session-booking',
            })
            setIsComplete(!isComplete)
            setPendingButtonAction(null)
          } else if (prepareLessonSuccess === false) {
            const errorMessage =
              prepareLessonMessage ||
              'Failed to book session. Please try again.'
            enqueueSnackbar(errorMessage, {
              variant: 'error',
            })
            setPendingButtonAction(null)
          }
          return
        }

        if (stripePaymentFormRef.current?.validatePayment) {
          const isValid = await stripePaymentFormRef.current.validatePayment()
          if (!isValid) {
            setPendingButtonAction(null)
            return
          }
        }

        if (shouldShowWaiver) {
          setShowWaiver(true)
          setPendingButtonAction(null)
        } else {
          await stripePaymentFormRef.current.submitPayment()
          setPendingButtonAction(null)
        }
      }
      proceedWithAction()
    }
  }, [
    pendingButtonAction,
    clientSecret,
    clientSecretLoading,
    shouldShowWaiver,
    shouldDelayClientSecretFetch,
    paymentRequired,
    isComplete,
    setIsComplete,
    eventObject,
    enqueueSnackbar,
    prepareLessonSuccess,
    prepareLessonMessage,
  ])

  const form = useForm({
    resolver: yupResolver(
      validationSchema(paymentRequired, shouldShowWaiver, true),
    ),
    ...(shouldShowWaiver && {
      defaultValues: {
        agreeWaiver: false,
      },
    }),
  })
  const classes = useStyles()

  const handleButtonClick = async () => {
    if (stripePaymentFormRef?.current?.validatePayment) {
      const isValid = await stripePaymentFormRef.current.validatePayment()
      if (!isValid) return
    }

    if (shouldShowWaiver) {
      setShowWaiver(true)
      return
    }

    // Trigger prepareLessonForStudent if it was delayed
    if (shouldDelayClientSecretFetch && !shouldFetchClientSecret) {
      setShouldFetchClientSecret(true)
      setPendingButtonAction(true)
      return
    }

    return stripePaymentFormRef?.current?.submitPayment()
  }

  const handleWaiverSubmit = async () => {
    if (paymentRequired) {
      if (stripePaymentFormRef.current?.submitPayment) {
        return stripePaymentFormRef.current.submitPayment()
      }
      return
    }

    setShouldFetchClientSecret(true)
    setPendingButtonAction(true)
  }

  if (clientSecretError) return <Error error={clientSecretError} />

  // Handle prepareLessonForStudent errors
  if (!clientSecretLoading && prepareLessonSuccess === false) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" m={2}>
        <RouterLinkButton
          variant="outlined"
          className={classes.bookingButton}
          style={{ marginBottom: '1rem' }}
          to={bookNewSessionUrl}
          data-testid="retry-booking-button"
          size="small"
        >
          Retry Booking
        </RouterLinkButton>
        <Error message={prepareLessonMessage || 'An error occurred.'} />
      </Box>
    )
  }

  return (
    <>
      <WaiverContainer showWaiver={showWaiver}>
        <CoachingCenterWaiver
          form={form}
          showWaiver={showWaiver}
          setShowWaiver={setShowWaiver}
          onSubmit={handleWaiverSubmit}
          withBackButton={true}
        />
      </WaiverContainer>
      <ContainerRoot>
        <ThemeProvider theme={extendTheme(themes.offeringsRefresh)}>
          <Grid container style={{ display: showWaiver ? 'none' : 'block' }}>
            <ProgressIndicator value={progressValue} />
            <BackButton previousUrl={previousUrl} />
            <CoachLessonHeader lessonType={lessonType} coach={coach} />
            <Grid item xs={12}>
              <Divider className={classes.divider} />
            </Grid>
            <Grid container>
              <Grid item md={7} xs={12} className={classes.gridItem}>
                <StepDescriptionOfferings
                  stepText={stepText}
                  title="Review"
                  description="Please review the information below."
                />
                <Grid
                  container
                  item
                  xs={12}
                  md={5}
                  className={classes.studentCard}
                >
                  <InitialsAvatar
                    name={`${student.firstName} ${student.lastName}`}
                    classes={classes}
                  />
                  <div className={classes.infoWrapped}>
                    <Typography
                      variant="caption"
                      className={`${classes.fontBolded} ${classes.captionText}`}
                      data-testid="student-name"
                    >
                      {studentName}
                    </Typography>
                    <Typography
                      variant="caption"
                      className={`${classes.captionText} ${classes.lighterText}`}
                    >
                      Student
                    </Typography>
                  </div>
                </Grid>
                <Divider style={{ margin: '2rem 0px 1rem' }} />
                {priceInDollars !== null && (
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography
                        variant="body1"
                        className={classes.lighterText}
                      >
                        Subtotal
                      </Typography>
                      <Typography
                        variant="body1"
                        className={classes.lighterText}
                      >
                        {currency(priceInDollars)}
                      </Typography>
                    </Box>
                    {paymentRequired ? (
                      <Box
                        display="flex"
                        className={classes.infoRow}
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography
                          variant="body1"
                          className={`${classes.fontBolded}`}
                        >
                          Due Today
                        </Typography>
                        <Typography
                          variant="body1"
                          className={`${classes.fontBolded}`}
                        >
                          {currency(priceInDollars)}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Box
                          display="flex"
                          className={classes.infoRow}
                          justifyContent="space-between"
                        >
                          <Typography
                            variant="body1"
                            className={`${classes.fontBolded}`}
                          >
                            Due Today
                          </Typography>
                          <Typography
                            variant="body1"
                            className={`${classes.fontBolded}`}
                          >
                            {currency(0)}
                          </Typography>
                        </Box>
                        <div className={classes.infoRow}>
                          <Typography
                            variant="body2"
                            className={`${classes.captionText} ${classes.lighterText}`}
                          >
                            Pay at location
                          </Typography>
                        </div>
                      </>
                    )}
                    <Divider style={{ margin: '1rem 0px 1rem' }} />
                  </Box>
                )}
                <FormProvider {...form}>
                  <form>
                    <Box mb={4}>
                      <StripePaymentElementsForm
                        ref={stripePaymentFormRef}
                        clientSecret={clientSecret}
                        secretLoading={clientSecretLoading}
                        coachSlug={idOrSlug}
                        studentId={student.id}
                        marketingOptInChecked={
                          userIsNotOptedIn ? marketingOptInChecked : null
                        }
                        eventObject={eventObject}
                        paymentRequired={paymentRequired}
                        setPaymentRequired={setPaymentRequired}
                        lessonType={lessonType}
                      />
                    </Box>
                    {userIsNotOptedIn && !coachBooksOnBehalfOfStudent && (
                      <MarketingOptIn
                        checked={marketingOptInChecked}
                        onChange={setMarketingOptInChecked}
                        useMarketingOptIn={'callout'}
                      />
                    )}
                    <Grid item>
                      <Button
                        variant="outlined"
                        className={classes.bookingButton}
                        onClick={handleButtonClick}
                        data-testid="complete-booking-button"
                        disabled={form.formState.isSubmitting}
                      >
                        {shouldShowWaiver ? 'Continue' : 'Complete Purchase'}
                      </Button>
                    </Grid>
                  </form>
                </FormProvider>
              </Grid>
            </Grid>
          </Grid>
        </ThemeProvider>
      </ContainerRoot>
    </>
  )
}

LessonReviewLayout.propTypes = {
  lessonType: PropTypes.object.isRequired,
  locationId: PropTypes.string.isRequired,
  startDateTime: PropTypes.string.isRequired,
  student: PropTypes.object.isRequired,
  previousUrl: PropTypes.string,
  isComplete: PropTypes.bool,
  setIsComplete: PropTypes.func,
  progressValue: PropTypes.number.isRequired,
  stepText: PropTypes.string.isRequired,
}

LessonReviewLayout.defaultProps = {
  isComplete: false,
}

export default LessonReviewLayout
